/**
 * Page détail courtier — `/brokers/:id` (refonte 9.P.3.C pattern Companies R1+R2).
 *
 * Pattern UX commun :
 *   - sticky page header `top-16 lg:top-0` + breadcrumb + H1 + status badge + role
 *   - 2-col Detail `lg:grid-cols-3` (main 2/3 + side panel 1/3 account)
 *   - CardSection moderne par section
 *   - BrokerCompaniesList intégré section
 *   - max-w-screen-xl
 *
 * Read-only. Logique métier : useBrokerWithProfile + BrokerCompaniesList préservés.
 * Pas couvert : édition profil + change role + soft-delete (reportés).
 */

import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useBrokerWithProfile } from "../../lib/queries/brokers";

import { BrokerCompaniesList } from "./_components/BrokerCompaniesList";
import { BrokerStatusBadge } from "./_components/BrokerStatusBadge";

export function BrokerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const { data, isLoading, error } = useBrokerWithProfile(id);

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-screen-xl">
        <div className="h-32 rounded-xl bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-screen-xl">
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {error instanceof ApiException
            ? getApiErrorMessage(error, lang) || t("errors.generic")
            : t("errors.generic")}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const profile = data.profile;

  return (
    <div className="mx-auto w-full max-w-screen-xl">
      {/* Sticky header */}
      <header className="sticky top-16 z-10 -mx-4 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:-mx-8 lg:top-0 lg:px-8 lg:py-5">
        <nav aria-label="breadcrumb" className="mb-2 text-sm">
          <Link
            to="/brokers"
            className="rounded text-slate-600 hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            ← {t("brokers.list.title")}
          </Link>
        </nav>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {data.full_name ?? data.email}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <BrokerStatusBadge status={data.status} />
          <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
            {t(`brokers.list.roles.${data.role}`, { defaultValue: data.role })}
          </span>
          {data.full_name && (
            <span className="text-sm text-slate-600">{data.email}</span>
          )}
        </div>
      </header>

      {/* 2-col Detail */}
      <div className="px-0 py-6 lg:grid lg:grid-cols-3 lg:gap-8 lg:py-8">
        {/* Main column 2/3 */}
        <div className="space-y-5 lg:col-span-2">
          <CardSection title={t("brokers.detail.section.profile")}>
            {profile ? (
              <>
                <Field
                  label={t("brokers.detail.fields.license_number")}
                  value={profile.license_number}
                />
                <Field
                  label={t("brokers.detail.fields.license_expiry")}
                  value={profile.license_expiry}
                />
                <Field
                  label={t("brokers.detail.fields.specialties")}
                  value={
                    profile.specialties && profile.specialties.length > 0
                      ? profile.specialties.join(", ")
                      : null
                  }
                />
                {profile.bio && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs text-slate-500">
                      {t("brokers.detail.fields.bio")}
                    </dt>
                    <dd className="mt-0.5 whitespace-pre-wrap text-sm text-slate-800">
                      {profile.bio}
                    </dd>
                  </div>
                )}
                {profile.signature_block && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs text-slate-500">
                      {t("brokers.detail.fields.signature_block")}
                    </dt>
                    <dd className="mt-0.5">
                      <pre className="whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-xs text-slate-700">
                        {profile.signature_block}
                      </pre>
                    </dd>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500 sm:col-span-2">
                {t("brokers.detail.no_profile")}
              </p>
            )}
          </CardSection>

          <CardSection title={t("brokers.detail.section.companies")}>
            <div className="sm:col-span-2">
              <BrokerCompaniesList brokerId={data.id} />
            </div>
          </CardSection>
        </div>

        {/* Side panel 1/3 — account */}
        <aside className="mt-5 space-y-5 lg:col-span-1 lg:mt-0">
          <CardSection title={t("brokers.detail.section.account")} sideStyle>
            <FieldStacked
              label={t("brokers.detail.fields.locale")}
              value={data.locale === "fr" ? "Français" : "English"}
            />
            <FieldStacked
              label={t("brokers.detail.fields.last_login")}
              value={data.last_login_at ? data.last_login_at.slice(0, 10) : null}
            />
            <FieldStacked
              label={t("brokers.detail.fields.created_at")}
              value={data.created_at.slice(0, 10)}
            />
          </CardSection>
        </aside>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function CardSection({
  title,
  children,
  sideStyle = false,
}: {
  title: string;
  children: React.ReactNode;
  sideStyle?: boolean;
}) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <header className="border-b border-slate-100 px-5 py-3.5">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      </header>
      <div className="px-5 py-4">
        <dl className={sideStyle ? "space-y-3" : "grid gap-x-6 gap-y-4 sm:grid-cols-2"}>
          {children}
        </dl>
      </div>
    </section>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  const display = value !== null && value !== undefined && value !== "" ? value : null;
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-800">
        {display === null ? <span className="text-slate-400">—</span> : display}
      </dd>
    </div>
  );
}

function FieldStacked({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  const display = value !== null && value !== undefined && value !== "" ? value : null;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-sm font-medium text-slate-800">
        {display ?? <span className="text-slate-400">—</span>}
      </dd>
    </div>
  );
}
